/**
 * Book → Companion & Wellness or Reading & Services (Eli, 17 Oct 2026 — the volunteer booking redesign,
 * claude/spec "Volunteer Booking Redesign"). One question per screen; Back keeps every answer.
 *
 * Companion & Wellness asks first: One-to-one or Group outing. A group outing is its own flow (GroupOutingSheet).
 * One-to-one: who → the plan (walk, store, pharmacy, errands, other — the plan sets the activity on the reports) →
 * date, time (5-minute steps), length, one time or every week, notes → where (from 135 West 23rd Street to a place on
 * the list, or a new one, which joins the list) → how they get there → anyone else coming → the volunteers.
 * Reading & Services: who → Reading or Lesson → date, time, length, one time or every week, notes → the volunteers.
 *
 * The volunteers step lists only volunteers Ready for the program — free at that day and time first, then the rest.
 * The office picks one and emails them to confirm, or emails everyone in the program, or saves it without sending.
 * The tenant is never named in a volunteer's email before the match is confirmed (then first name only).
 * The enrollment stop (shared/onboarding.ts requestGate) and the second person (1.4.0 §2) are unchanged.
 */
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CATEGORIES, CONSISTENT_WEEKS, MAX_STOPS, DEFAULT_DURATION_MINUTES, GuestFirstNameSchema, OTHER_WHAT_MAX, REQUEST_PLANS, START_ADDRESS, TRANSPORT_KINDS, areaCheck, directionsLinks, minutesLabel, type CategoryKey, type RequestInput, type RequestPlan, type TransportKind, type TripKind } from '@shared/volunteers';
import type { BookProgram } from '@shared/book-programs';
import type { SendResult, RequestView, TenantGateView } from '@shared/volunteers-api';
import type { TenantSummary } from '@shared/api-types';
import { apiFetch } from '@/lib/api';
import { useMe } from '@/auth/useMe';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { TenantSearch } from '@/components/TenantSearch';
import { TimeSelect } from '@/components/TimeSelect';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { StepHead, SummaryCard } from '@/components/Steps';
import { formatDayMedium } from '@/lib/dates';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { SelectField, TextArea, TextField } from '@/features/people/fields';
import { postJson, usePicker, useTenantGate, useVolunteerRefresh } from './api';
import { requestGate } from '@shared/onboarding';
import { pieceList } from '@/features/agreements/onboarding';
import { ProgramPicker } from '@/features/booking/ProgramPicker';
import { VolunteerChooser, orderedVolunteers, savePlace } from './VolunteerChooser';
import { UsualPartnerCard, useUsualPartner } from './UsualPartnerCard';
import { GroupOutingSheet } from './GroupOutingSheet';

type Dest = { name: string; address: string; approved: boolean };
const areaOfDest = (d: Dest | null | undefined) => (d?.address ? areaCheck(d.address) : d?.name ? areaCheck(d.name) : null);

interface Place {
  category: string;
  name: string;
  address: string;
  retired?: boolean;
}

export function useApprovedPlaces() {
  return useQuery({ queryKey: ['approved-places'], queryFn: () => apiFetch<{ count: number; places: Place[] }>('/api/settings/approved-places'), staleTime: 5 * 60_000 });
}

const iso = (k: string) => `${k.slice(0, 4)}-${k.slice(4, 6)}-${k.slice(6, 8)}`;

export function DestinationPicker({
  value,
  onChange,
  idPrefix,
  required,
  error,
}: {
  value: { name: string; address: string; approved: boolean } | null;
  onChange: (v: { name: string; address: string; approved: boolean } | null) => void;
  idPrefix: string;
  required?: boolean;
  error?: string;
}) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const places = useApprovedPlaces();
  const [filter, setFilter] = useState('');
  const [other, setOther] = useState(false);
  const [added, setAdded] = useState<string | null>(null);
  const live = useMemo(() => (places.data?.places ?? []).filter((p) => !p.retired), [places.data]);
  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return f ? live.filter((p) => `${p.name} ${p.address} ${p.category}`.toLowerCase().includes(f)) : live;
  }, [live, filter]);
  const groups = useMemo(() => {
    const m = new Map<string, Place[]>();
    for (const p of shown) m.set(p.category, [...(m.get(p.category) ?? []), p]);
    return [...m.entries()];
  }, [shown]);
  const key = (p: Place) => `${p.name}|${p.address}`;
  const selected = value && value.approved ? `${value.name}|${value.address}` : other ? '__other' : '';
  const area = value?.address ? areaCheck(value.address) : value?.name ? areaCheck(value.name) : null;
  const dirs = value?.address ? directionsLinks(value.address) : null;

  const addToList = async () => {
    if (!value?.name || !value.address) return;
    try {
      await postJson('/api/volunteers/places', { category: t('vol.req.addedCategory'), name: value.name, address: value.address, phone: '', notes: '' });
      const m = t('vol.req.placeAdded', { name: value.name });
      setAdded(m);
      announce(m);
      void places.refetch();
    } catch (e) {
      const m = errorText(e);
      setAdded(m);
      announce(m, 'assertive');
    }
  };

  return (
    <div className="field" data-testid={`${idPrefix}-dest`}>
      <label htmlFor={`${idPrefix}-dfilter`}>{t('vol.req.destFilter')}</label>
      <input id={`${idPrefix}-dfilter`} className="input" type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('vol.req.destFilterPh')} autoComplete="off" />
      <label htmlFor={`${idPrefix}-dest-select`}>
        {t('vol.req.dest')}
        {required ? <span className="req"> <span aria-hidden="true">*</span><span className="sr-only">{t('people.required')}</span></span> : null}
      </label>
      <select
        id={`${idPrefix}-dest-select`}
        className="input"
        value={selected}
        aria-invalid={error ? true : undefined}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '__other') {
            setOther(true);
            onChange({ name: '', address: '', approved: false });
            return;
          }
          setOther(false);
          const p = live.find((x) => key(x) === v);
          onChange(p ? { name: p.name, address: p.address, approved: true } : null);
        }}
      >
        <option value="">{t('vol.req.destChoose', { count: live.length })}</option>
        {groups.map(([g, ps]) => (
          <optgroup key={g} label={g}>
            {ps.map((p) => (
              <option key={key(p)} value={key(p)}>
                {p.name} · {p.address}
              </option>
            ))}
          </optgroup>
        ))}
        <option value="__other">{t('vol.req.destOther')}</option>
      </select>
      {other || (value && !value.approved) ? (
        <div className="grid-2">
          <TextField id={`${idPrefix}-oname`} label={t('vol.req.placeName')} value={value?.name ?? ''} onChange={(v) => onChange({ name: v, address: value?.address ?? '', approved: false })} required={required} />
          <TextField id={`${idPrefix}-oaddr`} label={t('vol.req.placeAddress')} value={value?.address ?? ''} onChange={(v) => onChange({ name: value?.name ?? '', address: v, approved: false })} />
        </div>
      ) : null}
      {dirs ? (
        <div className="dirs" data-testid={`${idPrefix}-dirs`}>
          <span className="help">{t('vol.req.dirsLabel')}</span>
          <a href={dirs.google} target="_blank" rel="noopener noreferrer">
            <Icon name="arrow" /> Google Maps
          </a>
          <a href={dirs.apple} target="_blank" rel="noopener noreferrer">
            <Icon name="arrow" /> Apple Maps
          </a>
          {value && !value.approved && value.name && value.address ? (
            <Button type="button" variant="secondary" size="sm" onClick={() => addToList()} data-testid={`${idPrefix}-add-place`}>
              {t('vol.req.addApproved')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {added ? <p role="status" className="help">{added}</p> : null}
      {area === 'out' ? (
        <p className="warn" role="alert" data-testid={`${idPrefix}-outside`}>
          <Icon name="alert" /> <span>{t('vol.req.outside')}</span>
        </p>
      ) : area === 'unknown' && value?.name ? (
        <p className="help">{t('vol.req.areaUnknown')}</p>
      ) : null}
      <p className="help">{t('vol.req.destHelp', { count: live.length })}</p>
    </div>
  );
}

/** How they get there (Eli, 17 Oct 2026): walking, an assisted ride, Uber, the bus or the train. */
export function TransportPicker({ value, onChange, idPrefix }: { value: TransportKind | null; onChange: (v: TransportKind) => void; idPrefix: string }) {
  const { t } = useTranslation();
  return (
    <div className="cat-cards" role="group" aria-label={t('vb.transport')} data-testid={`${idPrefix}-transport`}>
      {TRANSPORT_KINDS.map((k) => (
        <button key={k} type="button" aria-pressed={value === k} onClick={() => onChange(k)} data-testid={`${idPrefix}-transport-${k}`}>
          <b>{t(`vb.tr.${k}`)}</b>
        </button>
      ))}
    </div>
  );
}

/** Lengths offered (Eli: reading is 30, 40, 50, 60 or 120 minutes). */
const LENGTHS = { READING: [30, 40, 50, 60, 120], COMPANION: [30, 45, 60, 90, 120] } as const;

type ReqStep = 'kind' | 'who' | 'what' | 'when' | 'where' | 'how' | 'second' | 'confirm';
type Chosen = { tenantId: string; unit: string; name: string };

export function RequestSheet({
  onClose,
  returnFocusTo,
  todayKey,
  service = 'COMPANION',
  tenant: given = null,
  onProgram,
}: {
  onClose: () => void;
  returnFocusTo: RefObject<HTMLElement | null>;
  todayKey: string;
  /** The service chosen on Book (1.8.3). */
  service?: 'COMPANION' | 'READING';
  /** The tenant already chosen on Book: "Who is it for?" is skipped. */
  tenant?: Chosen | null;
  /** Book's "what" row: another program was pressed (the tenant chosen so far goes with it). */
  onProgram?: (program: BookProgram, tenant: Chosen | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const companion = service === 'COMPANION';
  const [kind, setKind] = useState<'one' | 'group' | null>(companion ? null : 'one');
  const [step, setStep] = useState<ReqStep>(companion ? 'kind' : given ? 'what' : 'who');
  const [tenant, setTenant] = useState<Chosen | null>(given);
  const [plan, setPlan] = useState<RequestPlan | null>(null);
  const [otherWhat, setOtherWhat] = useState('');
  const [day, setDay] = useState(iso(todayKey));
  const [minutes, setMinutes] = useState(14 * 60);
  const [duration, setDuration] = useState(DEFAULT_DURATION_MINUTES);
  const [mode, setMode] = useState<'one-time' | 'consistent'>('one-time');
  const [weeks, setWeeks] = useState(8);
  const [dest, setDest] = useState<{ name: string; address: string; approved: boolean } | null>(null);
  // D-1028-COMPANION part 2: stops 2 to 4 (stop 1 is `dest`)
  const [more, setMore] = useState<Dest[]>([]);
  const [transport, setTransport] = useState<TransportKind | null>(null);
  const [notes, setNotes] = useState('');
  const [secondKind, setSecondKind] = useState<'none' | 'tenant' | 'guest'>('none');
  const [secondTenant, setSecondTenant] = useState<{ tenantId: string; name: string; unit: string } | null>(null);
  const [guest, setGuest] = useState('');
  const [anyoneElse, setAnyoneElse] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  // D-1028-COMPANION: "A volunteer already agreed" — the save is the Confirm; the volunteer's own email is a tick, off
  const role = useMe().data?.role;
  const [arranged, setArranged] = useState(false);
  const [sendDetails, setSendDetails] = useState(false);
  const [trained, setTrained] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const gate = useTenantGate(tenant?.tenantId ?? null);
  const gate2 = useTenantGate(secondKind === 'tenant' ? (secondTenant?.tenantId ?? null) : null);
  const dayKey = day.replace(/-/g, '');
  const plans = REQUEST_PLANS[service];
  const category: CategoryKey | null = plan?.category ?? null;
  const trip: TripKind = plan?.trip ?? 'store';
  const cat: CategoryKey = category ?? plans[0]!.category;
  const pick = usePicker(dayKey.length === 8 && category ? { category, dayKey, minutes, duration } : null);
  const def = CATEGORIES[cat];
  const area = dest?.address ? areaCheck(dest.address) : dest?.name ? areaCheck(dest.name) : null;
  const g = gate.data;
  const programName = companion ? t('vol.program.companion') : t('vol.program.reading');
  // 1.6.7: which gate refuses is decided by the one shared rule (shared/onboarding.ts requestGate), as on the server
  const GATE_KEY = { waiver: 'vol.req.gateWaiver', mobility: 'vol.req.gateMobility', active: 'vol.req.gateActive' } as const;
  const gateOf = (x: TenantGateView | undefined) => {
    if (x && (companion ? x.companion.paused : x.reading.paused)) return t('pause.gate', { name: x.name, program: programName });
    const r = x ? requestGate(x, companion) : null;
    if (!r || !x) return null;
    if (r.why !== 'onboarding') return t(GATE_KEY[r.why], { name: x.name });
    const o = r.onboarding;
    return t('onb167.reason', { name: x.name, program: t(`onb167.program.${o.program}`), done: o.done, total: o.total, list: pieceList(t, i18n.language, o.missing) });
  };
  const stopWhy = gateOf(g);
  const stopped = !!g && !!stopWhy;
  const stopLine = g ? t('book183.stop.line', { name: g.firstName || g.name, program: programName }) : '';
  const asksSecond = companion;
  const household = g?.household ?? [];
  const gate2Msg = asksSecond && secondKind === 'tenant' && secondTenant ? gateOf(gate2.data) : null;
  const guestOk = GuestFirstNameSchema.safeParse(guest).success;
  const someoneComing = asksSecond && ((secondKind === 'tenant' && !!secondTenant) || (secondKind === 'guest' && guestOk));
  const needsOk = someoneComing && anyoneElse;
  const goesOut = def.destination !== 'none';
  const canArrange = companion && (role === 'admin' || role === 'coadmin') && !needsOk;
  const ordered = orderedVolunteers(pick.data);
  const allCount = ordered.free.length + ordered.rest.length;
  const chosenName = chosen ? [...ordered.free, ...ordered.rest].find((v) => v.volunteerId === chosen)?.name ?? null : null;
  const steps: ReqStep[] = [...(companion ? (['kind'] as const) : []), 'who', 'what', 'when', ...(goesOut ? (['where', 'how'] as const) : []), ...(asksSecond ? (['second'] as const) : []), 'confirm'];
  const n = steps.indexOf(step) + 1;
  const go = (s: ReqStep) => {
    setProblem(null);
    setStep(s);
  };
  const after = (s: ReqStep): ReqStep => {
    const nx = steps[steps.indexOf(s) + 1] ?? 'confirm';
    return nx === 'who' && tenant ? 'what' : nx;
  };
  const before = (s: ReqStep): ReqStep => {
    const pv = steps[Math.max(0, steps.indexOf(s) - 1)]!;
    return pv === 'who' && given ? (companion ? 'kind' : 'who') : pv;
  };
  const refuse = (m: string) => {
    setProblem(m);
    announce(m, 'assertive');
  };
  useEffect(() => {
    if (step === 'what' && stopped) announce(stopLine, 'assertive');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, stopped, stopLine]);
  // D-1103-PAIR: "Usually with ..." - a volunteer picked from the card travels with the day and time it fills in
  const keepPick = useRef<string | null>(null);
  const usual = useUsualPartner({ tenantId: step === 'confirm' ? (tenant?.tenantId ?? null) : null, category, dayKey, minutes });
  // a volunteer picked for another day or time is not kept
  useEffect(() => {
    setChosen(keepPick.current);
    keepPick.current = null;
  }, [dayKey, minutes, category]);

  const missing = (s: ReqStep): string | null => {
    if (s === 'kind') return kind ? null : t('vb.needKind');
    if (s === 'who') return tenant ? null : t('vol.req.needTenant');
    if (s === 'what') {
      if (stopped) return stopLine;
      if (!g) return t('book183.checking');
      if (!plan) return t('book183.needPlan');
      return plan.key === 'other' && !otherWhat.trim() ? t('book183.other.need') : null;
    }
    if (s === 'when') return /^\d{8}$/.test(dayKey) ? null : t('activities.needDate');
    if (s === 'where') {
      if (def.destination === 'required' && !dest?.name) return t('vol.req.needDest');
      if (area === 'out') return t('vol.req.outside');
      for (const [i, x] of more.entries()) {
        if (!x.name.trim()) return t('stops1028.needName', { n: i + 2 });
        if (areaOfDest(x) === 'out') return t('stops1028.outside', { n: i + 2, name: x.name });
      }
      return null;
    }
    if (s === 'how') return transport ? null : t('vb.needTransport');
    if (s === 'second') return secondKind !== 'none' && !someoneComing ? (secondKind === 'guest' && guest.trim() ? t('vol.req.guestOnly') : t('vol.req.needSecond')) : gate2Msg;
    return null;
  };
  const next = () => {
    const m = missing(step);
    if (m) return refuse(m);
    go(after(step));
  };
  const chooseKind = (k: 'one' | 'group') => {
    setKind(k);
    setProblem(null);
    if (k === 'one') go(tenant ? 'what' : 'who');
  };
  const choosePlan = (p: RequestPlan) => {
    if (stopped || !g) return;
    setPlan(p);
    setProblem(null);
    if (CATEGORIES[p.category].destination === 'none') {
      setDest(null);
      setMore([]);
    }
    if (p.key !== 'other') go('when');
  };

  const send = async (audience: 'one' | 'all' | 'none' | 'arranged') => {
    for (const s of steps) {
      const m = s === 'confirm' ? null : missing(s);
      if (m) {
        go(s);
        return refuse(m);
      }
    }
    if ((audience === 'one' || audience === 'arranged') && !chosen) return refuse(t('vb.needPick'));
    if (audience === 'arranged' && g?.companion.wheelchair && !trained) return refuse(t('arr1028.needTrained', { name: g.firstName || g.name }));
    const body: RequestInput = {
      tenantId: tenant!.tenantId,
      category: cat,
      trip: cat === 'trip' ? trip : null,
      ...(cat === 'other' ? { otherWhat: otherWhat.trim() } : {}),
      dayKey,
      minutes,
      duration,
      mode,
      weeks,
      destination: !goesOut || !dest?.name ? null : dest,
      ...(goesOut && dest?.name && more.length ? { moreStops: more.filter((x) => x.name.trim()) } : {}),
      transport: goesOut ? transport : null,
      notes,
      audience,
      ...(audience === 'one' ? { volunteerId: chosen! } : {}),
      ...(audience === 'arranged' ? { volunteerId: chosen!, sendVolunteerDetails: sendDetails, wheelchairTraining: trained } : {}),
      second: !someoneComing ? { kind: 'none' } : secondKind === 'tenant' ? { kind: 'tenant', tenantId: secondTenant!.tenantId } : { kind: 'guest', firstName: guest.trim() },
      anyoneElse: needsOk,
    };
    setBusy(true);
    try {
      if (goesOut) for (const x of [dest, ...more]) await savePlace(postJson, x, t('vol.req.addedCategory'));
      const r = await postJson<{ request: RequestView; send: SendResult }>('/api/volunteers/requests', body);
      const m = r.send.message;
      toast(m);
      announce(m);
      refresh();
      onClose();
    } catch (e) {
      refuse(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (kind === 'group') return <GroupOutingSheet onClose={onClose} returnFocusTo={returnFocusTo} todayKey={todayKey} first={tenant} onBack={() => { setKind(null); setStep('kind'); }} onProgram={onProgram ? (p) => onProgram(p, tenant) : undefined} />;

  const planWords = plan ? (plan.key === 'other' ? `${t('book183.plan.other')} · ${otherWhat.trim()}` : t(`book183.plan.${plan.key}`)) : '';
  const whoElse = !asksSecond ? null : someoneComing ? (secondKind === 'guest' ? t('vol.req.withGuest', { name: guest.trim() }) : t('vol.req.secondPicked', { unit: secondTenant!.unit, name: secondTenant!.name })) : t('vol.req.secondNo');
  const question =
    step === 'kind' ? t('vb.q.kind') : step === 'how' ? t('vb.q.how') : step === 'confirm' ? t('vb.q.volunteers') : step === 'what' ? (stopped ? t('book183.stop.q') : companion ? t('book183.q.plan') : t('vb.q.readWhat')) : t(`vol.req.q.${step}`);

  let body: React.ReactNode = null;
  if (step === 'kind') {
    body = (
      <div className="cat-cards book183-plans" role="group" aria-labelledby="req-q" data-testid="vb-kind">
        <button type="button" aria-pressed={kind === 'one'} onClick={() => chooseKind('one')} data-testid="vb-kind-one">
          <b>{t('vb.kind.one')}</b>
          <small>{t('vb.kind.oneSub')}</small>
        </button>
        <button type="button" aria-pressed={false} onClick={() => chooseKind('group')} data-testid="vb-kind-group">
          <b>{t('vb.kind.group')}</b>
          <small>{t('vb.kind.groupSub')}</small>
        </button>
      </div>
    );
  } else if (step === 'who') {
    body = (
      <div className="field">
        {tenant ? (
          <p className="picked" data-testid="req-tenant">
            <b>
              {tenant.unit} · {tenant.name}
            </b>{' '}
            <Button variant="secondary" size="sm" onClick={() => setTenant(null)}>
              {t('vol.req.change')}
            </Button>
          </p>
        ) : (
          <TenantSearch
            onOpen={(x: TenantSummary) => {
              setTenant({ tenantId: x.tenantId, unit: x.unit, name: x.name });
              go('what');
            }}
            label={t('vol.req.tenant')}
            placeholder={t('vol.req.tenantPh')}
            id="req-tenant-search"
            testId="req-tenant-search"
          />
        )}
      </div>
    );
  } else if (step === 'what') {
    body = (
      <>
        {tenant ? (
          <p className="picked" data-testid="req-tenant">
            <b>
              {tenant.unit} · {tenant.name}
            </b>{' '}
            <span data-testid="req-service">· {programName}</span>{' '}
            <Button variant="ghost" size="sm" onClick={() => { setTenant(null); go('who'); }}>
              {t('vol.req.change')}
            </Button>
          </p>
        ) : null}
        {!g ? (
          <p className="help" role="status" data-testid="req-checking">
            {gate.isError ? errorText(gate.error) : t('book183.checking')}
          </p>
        ) : stopped ? (
          <div className="warn book183-stop" role="alert" data-testid="req-gate-refused">
            <Icon name="lock" />
            <div>
              <b data-testid="req-stop">{stopLine}</b>
              <span className="book183-stop-why" data-testid="req-stop-why">
                {stopWhy}
              </span>
              <Link className="btn secondary sm" to={`/people/${encodeURIComponent(g.tenantId)}`} onClick={onClose} data-testid="req-open-card">
                {t('book183.stop.open', { name: g.firstName || g.name })}
              </Link>
            </div>
          </div>
        ) : (
          <>
            {g.companion.wheelchair && companion ? (
              <p className="help" data-testid="req-wheelchair">
                {t('vol.req.wheelchair')}
              </p>
            ) : null}
            <div className="cat-cards book183-plans" role="group" aria-labelledby="req-q">
              {plans.map((p) => (
                <button key={p.key} type="button" aria-pressed={plan?.key === p.key} onClick={() => choosePlan(p)} data-testid={p.testId}>
                  <b>{t(`book183.plan.${p.key}`)}</b>
                  <small>{t(`book183.planSub.${p.key}`)}</small>
                </button>
              ))}
            </div>
            {plan?.key === 'other' ? (
              <TextField
                id="req-other-what"
                label={t('book183.other.label')}
                help={t('book183.other.help')}
                value={otherWhat}
                onChange={(v) => setOtherWhat(v.replace(/[\r\n]+/g, ' '))}
                maxLength={OTHER_WHAT_MAX}
                required
                autoComplete="off"
              />
            ) : null}
          </>
        )}
      </>
    );
  } else if (step === 'when') {
    body = (
      <>
        <div className="grid-2">
          <TextField id="req-day" label={t('vol.req.day')} type="date" value={day} onChange={setDay} required />
          <SelectField id="req-duration" label={t('vol.req.duration')} value={String(duration)} onChange={(v) => setDuration(Number(v))} options={LENGTHS[service].map((m) => ({ value: String(m), label: t('vol.req.minutes', { count: m }) }))} />
        </div>
        <TimeSelect id="req-time" label={t('vol.req.time')} value={minutes} onChange={setMinutes} />
        <fieldset className="field">
          <legend className="eyebrow">{t('vol.req.how')}</legend>
          <div className="seg" role="group" aria-label={t('vol.req.how')}>
            <button type="button" aria-pressed={mode === 'one-time'} onClick={() => setMode('one-time')} data-testid="req-one-time">
              {t('vol.req.oneTime')}
            </button>
            <button type="button" aria-pressed={mode === 'consistent'} onClick={() => setMode('consistent')} data-testid="req-consistent">
              {t('vb.everyWeek')}
            </button>
          </div>
          {mode === 'consistent' ? <SelectField id="req-weeks" label={t('vol.req.forWeeks')} value={String(weeks)} onChange={(v) => setWeeks(Number(v))} options={CONSISTENT_WEEKS.map((w) => ({ value: String(w), label: t('vol.req.weeks', { count: w }) }))} /> : null}
        </fieldset>
        <TextArea id="req-notes" label={t('vb.notesForVolunteer')} value={notes} onChange={setNotes} />
      </>
    );
  } else if (step === 'where') {
    body = (
      <>
        <p className="help" data-testid="req-start">
          {t('vb.startFrom', { address: START_ADDRESS })}
        </p>
        <DestinationPicker value={dest} onChange={setDest} idPrefix="req" required={def.destination === 'required'} />
        {more.map((x, i) => (
          <fieldset className="field stop1028" key={i} data-testid={`req-stop-${i + 2}`}>
            <legend className="eyebrow">{t('stops1028.stopN', { n: i + 2 })}</legend>
            <DestinationPicker value={x} onChange={(v) => setMore((m) => m.map((y, j) => (j === i ? (v ?? { name: '', address: '', approved: false }) : y)))} idPrefix={`req-s${i + 2}`} required />
            <Button type="button" variant="secondary" size="sm" onClick={() => { setMore((m) => m.filter((_, j) => j !== i)); announce(t('stops1028.removed', { n: i + 2 })); }} data-testid={`req-stop-remove-${i + 2}`}>
              {t('stops1028.remove', { n: i + 2 })}
            </Button>
          </fieldset>
        ))}
        {dest?.name && more.length + 1 < MAX_STOPS ? (
          <Button type="button" variant="secondary" size="sm" onClick={() => { setMore((m) => [...m, { name: '', address: '', approved: false }]); announce(t('stops1028.added', { n: more.length + 2 })); }} data-testid="req-add-stop">
            {t('stops1028.add', { n: more.length + 2, max: MAX_STOPS })}
          </Button>
        ) : null}
        {dest?.name && more.length ? <p className="help">{t('stops1028.order')}</p> : null}
      </>
    );
  } else if (step === 'how') {
    body = (
      <div data-testid="req-how">
        <TransportPicker value={transport} onChange={(v) => { setTransport(v); setProblem(null); go(after('how')); }} idPrefix="req" />
      </div>
    );
  } else if (step === 'second') {
    body = (
      <>
        <div className="field" data-testid="req-second">
          <div className="seg" role="group" aria-label={t('vol.req.second')}>
            {(['none', 'tenant', 'guest'] as const).map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={secondKind === k}
                onClick={() => {
                  setSecondKind(k);
                  if (k === 'none') {
                    setAnyoneElse(false);
                    go('confirm');
                  }
                }}
                data-testid={`req-second-${k}`}
              >
                {t(k === 'none' ? 'vol.req.secondNo' : k === 'tenant' ? 'vol.req.secondTenant' : 'vol.req.secondGuest')}
              </button>
            ))}
          </div>
          {secondKind === 'tenant' ? (
            secondTenant ? (
              <p className="picked" data-testid="req-second-picked">
                <b>{t('vol.req.secondPicked', { unit: secondTenant.unit, name: secondTenant.name })}</b>{' '}
                <Button variant="secondary" size="sm" onClick={() => setSecondTenant(null)}>
                  {t('vol.req.change')}
                </Button>
              </p>
            ) : (
              <>
                {household.length ? (
                  <div className="chips dense" role="group" aria-label={t('vol.req.householdFirst')} data-testid="req-second-household">
                    {household.map((h) => (
                      <button key={h.tenantId} type="button" className="chip" onClick={() => setSecondTenant(h)} data-testid={`req-second-pick-${h.tenantId}`}>
                        {t('vol.req.pickHousehold', { name: h.name })}
                      </button>
                    ))}
                  </div>
                ) : null}
                <TenantSearch onOpen={(x) => setSecondTenant({ tenantId: x.tenantId, name: x.name, unit: x.unit })} label={t('vol.req.secondSearch')} placeholder={t('vol.req.secondSearchPh')} id="req-second-search" testId="req-second-search" />
              </>
            )
          ) : null}
          {secondKind === 'guest' ? <TextField id="req-guest" label={t('vol.req.guestName')} value={guest} onChange={setGuest} maxLength={40} autoComplete="off" help={t('vol.req.guestHelp')} error={guest.trim() && !guestOk ? t('vol.req.guestOnly') : undefined} /> : null}
          {gate2Msg ? (
            <p className="warn" role="alert" data-testid="req-second-refused">
              <Icon name="lock" /> <span>{gate2Msg}</span>
            </p>
          ) : null}
          <p className="help">{t('book183.secondHelp')}</p>
        </div>
        {someoneComing ? (
          <fieldset className="field" data-testid="req-anyone">
            <legend className="eyebrow">{t('vol.req.anyoneElse')}</legend>
            <div className="seg" role="group" aria-label={t('vol.req.anyoneElse')}>
              <button type="button" aria-pressed={!anyoneElse} onClick={() => setAnyoneElse(false)} data-testid="req-anyone-no">
                {t('vol.req.anyoneNo')}
              </button>
              <button type="button" aria-pressed={anyoneElse} onClick={() => setAnyoneElse(true)} data-testid="req-anyone-yes">
                {t('vol.req.anyoneYes')}
              </button>
            </div>
            {anyoneElse ? (
              <p className="warn" role="note" data-testid="req-needs-ok">
                <Icon name="lock" /> <span>{t('vol.req.needsOkNote')}</span>
              </p>
            ) : null}
          </fieldset>
        ) : null}
      </>
    );
  } else {
    body = (
      <>
        <SummaryCard
          testId="req-confirm"
          rows={[
            { label: t('vol.req.c.who'), value: tenant ? `${tenant.unit} · ${tenant.name}` : '' },
            { label: t('vol.req.c.what'), value: `${programName} · ${planWords}` },
            { label: t('vol.req.c.when'), value: `${/^\d{8}$/.test(dayKey) ? formatDayMedium(dayKey, lang) : '—'} · ${minutesLabel(minutes)} · ${t('vol.req.minutes', { count: duration })} · ${mode === 'one-time' ? t('vol.req.oneTime') : `${t('vb.everyWeek')} · ${t('vol.req.weeks', { count: weeks })}`}` },
            { label: t('vol.req.c.where'), value: !goesOut ? t('vol.req.inBuilding') : `${t('vb.fromTo', { from: START_ADDRESS, to: dest?.name ? [dest, ...more.filter((x) => x.name.trim())].map((x) => `${x.name}${x.address ? `, ${x.address}` : ''}`).join(` ${t('stops1028.then')} `) : t('vol.req.c.noPlace') })}${transport ? ` · ${t(`vb.tr.${transport}`)}` : ''}` },
            ...(whoElse ? [{ label: t('vol.req.c.second'), value: `${whoElse}${needsOk ? ` · ${t('vol.req.c.needsOk')}` : ''}` }] : []),
            ...(notes.trim() ? [{ label: t('vol.req.c.notes'), value: notes.trim() }] : []),
          ]}
        />
        {needsOk ? (
          <p className="warn" role="note" data-testid="req-needs-ok">
            <Icon name="lock" /> <span>{t('vol.req.needsOkNote')}</span>
          </p>
        ) : null}
        {canArrange ? (
          <fieldset className="field" data-testid="req-arranged">
            <label className="check">
              <input type="checkbox" checked={arranged} onChange={(e) => setArranged(e.target.checked)} data-testid="req-arranged-on" />
              <span>{t('arr1028.agreed')}</span>
            </label>
            <p className="help">{t('arr1028.agreedHelp')}</p>
            {arranged ? (
              <>
                <p className="help" data-testid="req-arranged-note">{chosenName ? t('arr1028.note', { name: chosenName.split(' ')[0] }) : t('arr1028.pickBelow')}</p>
                <label className="check">
                  <input type="checkbox" checked={sendDetails} onChange={(e) => setSendDetails(e.target.checked)} data-testid="req-arranged-details" />
                  <span>{t('arr1028.sendDetails')}</span>
                </label>
                {g?.companion.wheelchair ? (
                  <label className="check">
                    <input type="checkbox" checked={trained} onChange={(e) => setTrained(e.target.checked)} data-testid="req-arranged-trained" />
                    <span>{t('arr1028.trained', { name: g.firstName || g.name })}</span>
                  </label>
                ) : null}
              </>
            ) : null}
          </fieldset>
        ) : null}
        <UsualPartnerCard
          view={usual.data}
          onBook={(id, k, m) => {
            keepPick.current = id;
            setDay(iso(k));
            if (m !== null) setMinutes(m);
            // the same day and time as before: nothing changes, so nothing clears the pick
            if (k === dayKey && (m === null || m === minutes)) {
              keepPick.current = null;
              setChosen(id);
            }
          }}
        />
        <VolunteerChooser marked={usual.data?.enabled ? new Set(usual.data.marked) : undefined} pick={pick.data} loading={pick.isLoading} selected={chosen} onSelect={setChosen} program={programName} service={def.service} onClose={onClose} />
      </>
    );
  }

  return (
    <SheetContent
      title={service === 'READING' ? t('prog1025.reqReading') : t('vol.req.title')}
      wide
      returnFocusTo={returnFocusTo}
      data-testid="request-sheet"
      footer={
        <>
          {step === 'kind' || (step === 'who' && !companion) || (step === 'what' && given && !companion && !onProgram) ? (
            <Button variant="secondary" onClick={onClose}>
              {t('activities.cancel')}
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => go(before(step))} data-testid="req-back">
              {t('activities.back')}
            </Button>
          )}
          {step === 'confirm' && arranged && canArrange ? (
            <Button disabled={busy || !chosen} onClick={() => send('arranged')} data-testid="req-save-arranged">
              {chosenName ? t('arr1028.save', { name: chosenName.split(' ')[0] }) : t('arr1028.saveNone')}
            </Button>
          ) : step === 'confirm' ? (
            <>
              <Button variant="ghost" disabled={busy} onClick={() => send('none')} data-testid="req-save-pick">
                {t('vb.saveOnly')}
              </Button>
              <Button variant="secondary" disabled={busy || !allCount} onClick={() => send('all')} data-testid="req-send-all">
                {t('vb.emailAll', { program: programName })} · <span className="tnum">{allCount}</span>
              </Button>
              <Button disabled={busy || !chosen} onClick={() => send('one')} data-testid="req-send-one">
                {chosenName ? t('vb.emailOne', { name: chosenName }) : t('vb.emailOneNone')}
              </Button>
            </>
          ) : step === 'kind' || (step === 'how' && !transport) || (step === 'what' && (stopped || !g)) ? null : (
            <Button onClick={next} data-testid="req-next">
              {t('activities.next')}
            </Button>
          )}
        </>
      }
    >
      {onProgram ? <ProgramPicker value={companion ? 'companion' : 'reading'} onChange={(p) => onProgram(p, tenant)} /> : null}
      <StepHead n={n} of={steps.length} question={question} id="req-q" testId="req-question" />
      {body}
      {problem ? (
        <p className="field-err" role="alert" data-testid="req-problem">
          <Icon name="alert" width={16} height={16} /> {problem}
        </p>
      ) : null}
    </SheetContent>
  );
}
